import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface ChamaAccount {
  id: string;
  name: string;
  type: string;
  balance: number;
  accountNumber?: string;
  bankName?: string;
  status: 'active' | 'suspended' | 'closed';
  createdAt: string;
  lastTransaction?: string;
}

interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'transfer';
  amount: number;
  description: string;
  date: string;
  status: 'pending' | 'completed' | 'failed';
}

const ChamaAccountManagement: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [accounts, setAccounts] = useState<ChamaAccount[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('accounts');

  useEffect(() => {
    if (chamaId) {
      loadAccounts();
      loadTransactions();
    }
  }, [chamaId]);

  const loadAccounts = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/accounts`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setAccounts(data.data);
        } else {
          setAccounts([]);
        }
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
      setAccounts([]);
    }
  };

  const loadTransactions = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/account-transactions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setTransactions(data.data);
        } else {
          setTransactions([]);
        }
      }
    } catch (error) {
      console.error('Failed to load transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getAccountStatusColor = (status: string) => {
    switch (status) {
      case 'active': return colors.success;
      case 'suspended': return colors.warning;
      case 'closed': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const getAccountStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return '✅';
      case 'suspended': return '⚠️';
      case 'closed': return '❌';
      default: return '❓';
    }
  };

  const getTransactionStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return colors.success;
      case 'pending': return colors.warning;
      case 'failed': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const renderAccountsTab = () => (
    <div style={styles.tabContent}>
      {/* Account Summary */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>🏦</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {accounts.filter(a => a.status === 'active').length}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Active Accounts</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>💰</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {formatCurrency(accounts.reduce((sum, account) => sum + account.balance, 0))}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Balance</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>📊</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {transactions.length}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Transactions</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>📈</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {accounts.length > 0 ? formatCurrency(accounts.reduce((sum, account) => sum + account.balance, 0) / accounts.length) : formatCurrency(0)}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Avg Balance</div>
          </div>
        </div>
      </div>

      {/* Accounts List */}
      <div style={styles.accountsList}>
        {accounts.length > 0 ? (
          accounts.map(renderAccountCard)
        ) : (
          <div style={{ ...styles.emptyState, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div style={styles.emptyIcon}>🏦</div>
            <div style={{ ...styles.emptyTitle, color: colors.text }}>No accounts found</div>
            <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
              No bank accounts have been linked to this chama yet.
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderTransactionsTab = () => (
    <div style={styles.tabContent}>
      {/* Transaction Summary */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>💰</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {formatCurrency(transactions.filter(t => t.type === 'deposit').reduce((sum, t) => sum + t.amount, 0))}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Deposits</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>📉</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {formatCurrency(transactions.filter(t => t.type === 'withdrawal').reduce((sum, t) => sum + t.amount, 0))}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Withdrawals</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>✅</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {transactions.filter(t => t.status === 'completed').length}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Completed</div>
          </div>
        </div>

        <div style={{ ...styles.summaryCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.summaryIcon}>⏳</div>
          <div style={styles.summaryInfo}>
            <div style={{ ...styles.summaryValue, color: colors.text }}>
              {transactions.filter(t => t.status === 'pending').length}
            </div>
            <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Pending</div>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div style={styles.transactionsList}>
        {transactions.length > 0 ? (
          transactions.map(renderTransactionCard)
        ) : (
          <div style={{ ...styles.emptyState, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div style={styles.emptyIcon}>📊</div>
            <div style={{ ...styles.emptyTitle, color: colors.text }}>No transactions found</div>
            <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
              No account transactions have been recorded yet.
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderAccountCard = (account: ChamaAccount) => (
    <div key={account.id} style={{ ...styles.accountCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
      <div style={styles.accountHeader}>
        <div style={styles.accountInfo}>
          <div style={{ ...styles.accountName, color: colors.text }}>{account.name}</div>
          <div style={{ ...styles.accountType, color: colors.textSecondary }}>{account.type}</div>
        </div>
        <div style={{ ...styles.accountStatus, backgroundColor: getAccountStatusColor(account.status) + '20', border: `1px solid ${getAccountStatusColor(account.status)}` }}>
          <span style={{ fontSize: '16px', marginRight: '4px' }}>{getAccountStatusIcon(account.status)}</span>
          <span style={{ ...styles.statusText, color: getAccountStatusColor(account.status) }}>
            {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
          </span>
        </div>
      </div>

      <div style={styles.accountDetails}>
        <div style={styles.accountBalance}>
          <div style={{ ...styles.balanceLabel, color: colors.textSecondary }}>Balance</div>
          <div style={{ ...styles.balanceValue, color: colors.text }}>{formatCurrency(account.balance)}</div>
        </div>

        {account.accountNumber && (
          <div style={styles.accountNumber}>
            <div style={{ ...styles.numberLabel, color: colors.textSecondary }}>Account Number</div>
            <div style={{ ...styles.numberValue, color: colors.text }}>{account.accountNumber}</div>
          </div>
        )}

        {account.bankName && (
          <div style={styles.bankName}>
            <div style={{ ...styles.bankLabel, color: colors.textSecondary }}>Bank</div>
            <div style={{ ...styles.bankValue, color: colors.text }}>{account.bankName}</div>
          </div>
        )}
      </div>

      <div style={styles.accountMeta}>
        <div style={{ ...styles.createdDate, color: colors.textSecondary }}>
          Created: {new Date(account.createdAt).toLocaleDateString()}
        </div>
        {account.lastTransaction && (
          <div style={{ ...styles.lastTransaction, color: colors.textSecondary }}>
            Last transaction: {new Date(account.lastTransaction).toLocaleDateString()}
          </div>
        )}
      </div>

      <div style={styles.accountActions}>
        <button style={{ ...styles.actionButton, backgroundColor: colors.primary }}>
          View Details
        </button>
        <button style={{ ...styles.actionButton, backgroundColor: colors.success }}>
          Transfer Funds
        </button>
      </div>
    </div>
  );

  const renderTransactionCard = (transaction: Transaction) => (
    <div key={transaction.id} style={{ ...styles.transactionCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
      <div style={styles.transactionHeader}>
        <div style={styles.transactionType}>
          <div style={styles.typeIcon}>
            {transaction.type === 'deposit' ? '💰' : transaction.type === 'withdrawal' ? '📉' : '🔄'}
          </div>
          <div style={styles.typeInfo}>
            <div style={{ ...styles.transactionTypeText, color: colors.text }}>
              {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
            </div>
            <div style={{ ...styles.transactionDate, color: colors.textSecondary }}>
              {new Date(transaction.date).toLocaleDateString()}
            </div>
          </div>
        </div>
        <div style={{ ...styles.transactionStatus, backgroundColor: getTransactionStatusColor(transaction.status) + '20', border: `1px solid ${getTransactionStatusColor(transaction.status)}` }}>
          <span style={{ ...styles.statusText, color: getTransactionStatusColor(transaction.status) }}>
            {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
          </span>
        </div>
      </div>

      <div style={styles.transactionDetails}>
        <div style={styles.transactionAmount}>
          <div style={{ ...styles.amountLabel, color: colors.textSecondary }}>Amount</div>
          <div style={{ ...styles.amountValue, color: colors.text }}>{formatCurrency(transaction.amount)}</div>
        </div>
        <div style={styles.transactionDescription}>
          <div style={{ ...styles.descriptionLabel, color: colors.textSecondary }}>Description</div>
          <div style={{ ...styles.descriptionValue, color: colors.text }}>{transaction.description}</div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading account management...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Account Management"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Tab Navigation */}
          <div style={styles.tabContainer}>
            <button
              onClick={() => setActiveTab('accounts')}
              style={{
                ...styles.tabButton,
                backgroundColor: activeTab === 'accounts' ? colors.primary : 'transparent',
                color: activeTab === 'accounts' ? 'white' : colors.text,
              }}
            >
              🏦 Accounts
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              style={{
                ...styles.tabButton,
                backgroundColor: activeTab === 'transactions' ? colors.primary : 'transparent',
                color: activeTab === 'transactions' ? 'white' : colors.text,
              }}
            >
              📊 Transactions
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'accounts' ? renderAccountsTab() : renderTransactionsTab()}

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <button style={{ ...styles.actionButton, backgroundColor: colors.primary }}>
              Link Bank Account
            </button>
            <button style={{ ...styles.actionButton, backgroundColor: colors.success }}>
              Transfer Funds
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  tabContainer: {
    display: 'flex',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  tabButton: {
    flex: 1,
    padding: `${spacing.md} ${spacing.lg}`,
    borderRadius: borderRadius.lg,
    border: 'none',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  tabContent: {
    // Container for tab content
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  summaryCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    ...getShadowStyle('sm'),
  },
  summaryIcon: {
    fontSize: '24px',
    marginRight: spacing.md,
  },
  summaryInfo: {
    flex: 1,
  },
  summaryValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  accountsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  accountCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...getShadowStyle('sm'),
  },
  accountHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  accountInfo: {
    flex: 1,
  },
  accountName: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  accountType: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  accountStatus: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.md,
    display: 'flex',
    alignItems: 'center',
  },
  statusText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  accountDetails: {
    marginBottom: spacing.md,
  },
  accountBalance: {
    marginBottom: spacing.md,
  },
  balanceLabel: {
    fontSize: typography.fontSize.sm,
  },
  balanceValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    marginTop: spacing.xs / 2,
  },
  accountNumber: {
    marginBottom: spacing.md,
  },
  numberLabel: {
    fontSize: typography.fontSize.sm,
  },
  numberValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.xs / 2,
  },
  bankName: {
    marginBottom: spacing.md,
  },
  bankLabel: {
    fontSize: typography.fontSize.sm,
  },
  bankValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.xs / 2,
  },
  accountMeta: {
    marginBottom: spacing.md,
  },
  createdDate: {
    fontSize: typography.fontSize.sm,
  },
  lastTransaction: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  accountActions: {
    display: 'flex',
    gap: spacing.sm,
  },
  actionButton: {
    flex: 1,
    padding: spacing.sm,
    borderRadius: borderRadius.md,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  transactionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  transactionCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...getShadowStyle('sm'),
  },
  transactionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  transactionType: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  typeIcon: {
    fontSize: '24px',
    marginRight: spacing.md,
  },
  typeInfo: {
    flex: 1,
  },
  transactionTypeText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  transactionDate: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  transactionStatus: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.md,
  },
  transactionDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  transactionAmount: {
    flex: 1,
  },
  amountLabel: {
    fontSize: typography.fontSize.sm,
  },
  amountValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    marginTop: spacing.xs / 2,
  },
  transactionDescription: {
    flex: 2,
    marginLeft: spacing.md,
  },
  descriptionLabel: {
    fontSize: typography.fontSize.sm,
  },
  descriptionValue: {
    fontSize: typography.fontSize.base,
    marginTop: spacing.xs / 2,
  },
  emptyState: {
    textAlign: 'center',
    padding: spacing.xl,
    borderRadius: borderRadius.lg,
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
  },
  actionButtons: {
    display: 'flex',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
};

export default ChamaAccountManagement;